import { useMemo } from "react";
import {
  NotificationBellPopover,
  IconButton,
  type NotificationBellItem,
} from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import {
  formatNotificationRelativeTime,
  restaurantNotificationCopy,
} from "./notificationCopy";
import { parseRestaurantNotificationData } from "./notificationBehavior";
import {
  useOptionalRestaurantNotifications,
} from "./NotificationsProvider";
import { useOpenRestaurantNotification } from "./NotificationsRuntime";

export function RestaurantNotificationBell({
  triggerClassName,
}: {
  triggerClassName?: string;
}) {
  const t = useTranslate();
  const notifications = useOptionalRestaurantNotifications();
  const openNotification = useOpenRestaurantNotification();

  const items = useMemo<NotificationBellItem[]>(
    () =>
      (notifications?.notifications ?? []).map((notification) => {
        const parsed = parseRestaurantNotificationData({
          ...notification.data,
          type: notification.type,
        });
        const copy = restaurantNotificationCopy(
          { ...notification, data: parsed ?? undefined },
          t,
        );
        return {
          id: notification.id,
          title: copy.title,
          body: copy.body,
          read: notification.read,
          timeLabel: notification.createdAt
            ? formatNotificationRelativeTime(notification.createdAt.toDate(), t)
            : undefined,
        };
      }),
    [notifications?.notifications, t],
  );

  if (!notifications) return null;

  const notice = (() => {
    if (notifications.bootstrapStatus === "unavailable") {
      return {
        title: t((messages) => messages.notifications.feedUnavailableTitle),
        body: t((messages) => messages.notifications.bootstrapUnavailable),
        actionLabel: notifications.canRetryBootstrap
          ? t((messages) => messages.common.retry)
          : undefined,
        onAction: notifications.canRetryBootstrap
          ? notifications.retryBootstrap
          : undefined,
      };
    }
    if (notifications.error) {
      return {
        title: t((messages) => messages.notifications.feedUnavailableTitle),
        body: t((messages) => messages.notifications.feedUnavailable),
        actionLabel: t((messages) => messages.common.retry),
        onAction: notifications.retryFeed,
      };
    }
    if (notifications.status === "prompt") {
      return {
        title: t((messages) => messages.notifications.permissionPromptTitle),
        body: t((messages) => messages.notifications.permissionPromptBody),
        actionLabel: t((messages) => messages.notifications.permissionPromptAction),
        onAction: notifications.requestPermissionAndRegister,
      };
    }
    if (notifications.status === "denied") {
      return {
        title: t((messages) => messages.notifications.permissionPromptTitle),
        body: t((messages) => messages.notifications.permissionDenied),
      };
    }
    if (notifications.status === "unsupported") {
      return {
        title: t((messages) => messages.notifications.permissionPromptTitle),
        body: t((messages) => messages.notifications.unsupported),
      };
    }
    if (notifications.status === "error") {
      return {
        title: t((messages) => messages.notifications.permissionPromptTitle),
        body: t((messages) => messages.notifications.registrationError),
        actionLabel: t((messages) => messages.notifications.retryRegistration),
        onAction:
          notifications.lastError === "permission-request"
            ? notifications.requestPermissionAndRegister
            : notifications.retry,
      };
    }
    if (notifications.status === "registering") {
      return {
        title: t((messages) => messages.notifications.permissionPromptTitle),
        body: t((messages) => messages.common.loading),
        actionLabel: t((messages) => messages.notifications.permissionPromptAction),
        onAction: notifications.retry,
        actionLoading: true,
      };
    }
    return undefined;
  })();

  return (
    <div className="flex items-center gap-1">
      <IconButton
        size="sm"
        variant="ghost"
        shape="round"
        className={triggerClassName}
        ariaLabel={t((messages) =>
          notifications.soundStatus === "unsupported"
            ? messages.notifications.soundUnavailable
            : notifications.soundStatus === "ready"
              ? messages.notifications.muteSound
              : messages.notifications.enableSound,
        )}
        aria-pressed={notifications.soundStatus === "ready"}
        data-restaurant-notification-sound-control=""
        disabled={notifications.soundStatus === "unsupported"}
        onClick={() =>
          notifications.soundStatus === "ready"
            ? notifications.muteSound()
            : void notifications.enableSound()
        }
        icon={
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M11 5 6 9H3v6h3l5 4V5Z" />
            {notifications.soundStatus === "ready" ? (
              <>
                <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                <path d="M19 5a10 10 0 0 1 0 14" />
              </>
            ) : (
              <>
                <path d="m16 9 5 6" />
                <path d="m21 9-5 6" />
              </>
            )}
          </svg>
        }
      />
      <NotificationBellPopover
        ariaLabel={t((messages) => messages.notifications.title)}
        title={t((messages) => messages.notifications.title)}
        markAllReadLabel={t((messages) => messages.notifications.markAllRead)}
        emptyLabel={t((messages) => messages.notifications.empty)}
        items={items}
        unreadCount={notifications.unreadCount}
        onMarkAllRead={notifications.markAllAsRead}
        onItemClick={async (item) => {
          await notifications.markAsRead(item.id);
          const notification = notifications.notifications.find(
            (entry) => entry.id === item.id,
          );
          const parsed = notification
            ? parseRestaurantNotificationData({
                ...notification.data,
                type: notification.type,
              })
            : null;
          if (parsed) await openNotification(parsed);
        }}
        notice={notice}
        triggerClassName={triggerClassName}
      />
    </div>
  );
}
